import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUpdateEffect } from "react-use";
import { useSelectTheme } from "../../store";
import {
  SaveBar,
  PopupConfirmDiscardQuestionnaireChanges,
  QuestionnaireSectionV2,
  PopupReorderQuestionnaireSection,
  PopupEditQuestionnaireDisplayLogic,
  PopupWarningDisplayStrategyForbiddenOperation,
  createQuestionnaireInputDraftRegistry,
  QuestionnaireInputDraftContext,
} from "..";
import {
  DisplayLogicEditorDraftState,
  QuestionnaireEditorDraftState,
  QuestionnaireTempQuestion,
  QuestionnaireTempSection,
  TempQuestionnaire,
  TempQuestionnaireDisplayStrategyUpsertItem,
} from "../../model";
import { _, RIF, t } from "../../lib";
import { IQuestionnaire, QuestionnaireQuestionType } from "../../shared/db";
import { AddIcon } from "../../lib/svg/icon/add";

export enum QuestionnaireInputInitialValue {
  SectionTitle = "New Section Title",
  SectionDescription = "Description goes here",
  QuestionLabel = "New Question",
  QuestionOption = "New Option",
}

export interface QuestionnaireEditorDraftBinding {
  // holds a reader while the editor is open; it returns the working copy and the open editing context,
  // including text not blurred yet and logic entries still inside their debounce
  readerRef: MutableRefObject<(() => QuestionnaireEditorDraftState) | null>;
  onChange?: () => void;
  // working state to open with instead of the committed questionnaire
  restore?: QuestionnaireEditorDraftState | null;
}

export const QuestionnairePage = (props: {
  taskId?: string;
  questionnaire: TempQuestionnaire | IQuestionnaire | null;
  setQuestionnaire: Dispatch<SetStateAction<TempQuestionnaire | IQuestionnaire | null>>;
  closeAction: () => void;
  viewOnly: boolean;
  upsertDisplayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[];
  setUpsertDisplayStrategyList: Dispatch<
    SetStateAction<TempQuestionnaireDisplayStrategyUpsertItem[]>
  >;
  draft?: QuestionnaireEditorDraftBinding;
}) => {
  const { pad, color, fontSize, fontWeight } = useSelectTheme();
  const {
    questionnaire,
    setQuestionnaire,
    closeAction,
    viewOnly,
    upsertDisplayStrategyList,
    setUpsertDisplayStrategyList,
    draft,
  } = props;
  const restore = draft?.restore ?? null;
  // a restored editor opens on its saved working copy and editing block instead of the committed defaults
  const openedFromRestoreRef = useRef({
    sectionList: restore !== null,
    editingBlock: restore !== null,
  });
  useEffect(() => {
    if (!questionnaire) return;
    if (openedFromRestoreRef.current.editingBlock) {
      openedFromRestoreRef.current.editingBlock = false;
      return;
    }
    setEditingBlock(questionnaire.sectionList[0].id);
  }, [questionnaire]);
  const [tempSectionList, setTempSectionList] = useState<QuestionnaireTempSection[]>(() =>
    restore ? _.cloneDeep(restore.sectionList) : [],
  );
  const [renderPopupConfirmDiscard, setRenderPopupConfirmDiscard] = useState<boolean>(false);
  // edits change the working copy in place, so it must not share objects with the committed questionnaire
  // that Cancel returns to
  useMemo(() => {
    if (openedFromRestoreRef.current.sectionList) {
      openedFromRestoreRef.current.sectionList = false;
      return;
    }
    setTempSectionList(_.cloneDeep(questionnaire?.sectionList ?? []));
  }, [questionnaire]);
  const [renderPopupReorderSection, setRenderPopupReorderSection] = useState(false);
  const [renderPopupEditDisplayLogic, setRenderPopupEditDisplayLogic] = useState(
    Boolean(restore?.displayLogic),
  );
  const [displayLogicTargetType, setDisplayLogicTargetType] = useState<"question" | "section">(
    restore?.displayLogic?.targetType ?? "section",
  );
  const [editingLogicItemId, setEditingLogicItemId] = useState<string>(
    restore?.displayLogic?.itemId ?? "",
  );
  // the restored logic popup state applies only to the popup the editor reopens with
  const [displayLogicRestore, setDisplayLogicRestore] =
    useState<DisplayLogicEditorDraftState | null>(restore?.displayLogic ?? null);
  useUpdateEffect(() => {
    if (!renderPopupEditDisplayLogic) setDisplayLogicRestore(null);
  }, [renderPopupEditDisplayLogic]);

  const draftOnChangeRef = useRef(draft?.onChange);
  draftOnChangeRef.current = draft?.onChange;
  const [inputDraftRegistry] = useState(() =>
    draft
      ? createQuestionnaireInputDraftRegistry(restore?.inputBufferList ?? [], () =>
          draftOnChangeRef.current?.(),
        )
      : null,
  );
  const displayLogicReaderRef = useRef<(() => DisplayLogicEditorDraftState) | null>(null);
  const [
    renderPopupWarningDisplayStrategyForbiddenOperation,
    setRenderPopupWarningDisplayStrategyForbiddenOperation,
  ] = useState(false);
  const handleClosePopupWarningDisplayStrategyForbiddenOperation = () => {
    setRenderPopupWarningDisplayStrategyForbiddenOperation(false);
  };
  // track the id of editing block to control the ui
  const [editingBlock, setEditingBlock] = useState<string | null>(restore?.editingBlockId ?? null);

  useLayoutEffect(() => {
    if (!draft) return;
    const reader = (): QuestionnaireEditorDraftState => ({
      sectionList: tempSectionList,
      editingBlockId: editingBlock,
      inputBufferList: inputDraftRegistry?.readBufferList() ?? [],
      displayLogic: renderPopupEditDisplayLogic
        ? (displayLogicReaderRef.current?.() ?? null)
        : null,
    });
    draft.readerRef.current = reader;
    return () => {
      if (draft.readerRef.current === reader) draft.readerRef.current = null;
    };
  });

  useUpdateEffect(() => {
    draftOnChangeRef.current?.();
  }, [tempSectionList, editingBlock, renderPopupEditDisplayLogic]);
  // when click on background, make the editing block return to normal state
  const handleClickBackground = () => {
    setEditingBlock(null);
  };

  // when saving questionnaire, transform content list into the format of questionnaire
  const saveQuestionnaire = () => {
    setQuestionnaire({ ...questionnaire, sectionList: tempSectionList } as TempQuestionnaire);
    closeAction();
  };

  const handleCloseWithoutSaving = () => {
    if (!viewOnly && !_.isEqual(questionnaire?.sectionList, tempSectionList)) {
      return setRenderPopupConfirmDiscard(true);
    }
    closeAction();
  };

  const newSection = (): QuestionnaireTempSection => {
    const emptySection: QuestionnaireTempSection = {
      id: t.uuid(),
      legend: "",
      description: "",
      questionList: [],
    };
    if (questionnaire && "id" in questionnaire) {
      return { ...emptySection, questionnaireId: questionnaire.id };
    }
    return emptySection;
  };

  const newQuestion = (): QuestionnaireTempQuestion => {
    const emptyQuestion = {
      id: t.uuid(),
      label: t.uuid(),
      type: QuestionnaireQuestionType.MultiSelect,
      description: "New Question",
      required: true,
      inputConfig: {
        options: [{ name: "New Option", value: t.uuid() }],
      },
    };
    if (questionnaire && "id" in questionnaire) {
      return { ...emptyQuestion, questionnaireId: questionnaire.id };
    }
    return emptyQuestion;
  };

  const addSection = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    const newTempSectionList = [...tempSectionList];
    const section = newSection();
    newTempSectionList.splice(index + 1, 0, section);
    setTempSectionList(newTempSectionList.map((s, i) => ({ ...s, index: i })));
    setEditingBlock(section.id);
  };

  const addQuestion = (sectionId: string, index?: number) => {
    const newTempSectionList = [...tempSectionList];
    const i = newTempSectionList.findIndex(({ id }) => id === sectionId);
    if (i === -1) return;
    const question = newQuestion();
    if (!newTempSectionList[i].questionList) {
      newTempSectionList[i].questionList = [question];
    } else {
      if (index !== undefined) {
        newTempSectionList[i].questionList?.splice(index + 1, 0, question);
      } else {
        newTempSectionList[i].questionList?.push(question);
      }
    }
    setEditingBlock(question.id);
    setTempSectionList(newTempSectionList);
  };

  const AddSectionButton = (props: { index: number }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <button
        onClick={(e) => addSection(e, props.index)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        css={{
          width: "100%",
          height: "24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          border: "none",
          backgroundColor: "transparent",
          cursor: "pointer",
        }}
      >
        <div
          css={{
            width: "100%",
            height: 0,
            borderBottom: `1px solid ${hovered ? color.border.blue : color.border.default}`,
          }}
        />
        <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            css={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              border: `1px solid ${hovered ? color.textIcon.link : color.textIcon.secondary}`,
              backgroundColor: hovered ? color.textIcon.link : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AddIcon width={9} fill={hovered ? color.white : color.textIcon.secondary} />
          </div>
          <span
            css={{
              fontSize: fontSize.h7,
              fontWeight: fontWeight.bold,
              color: hovered ? color.textIcon.link : color.textIcon.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Add Section
          </span>
        </div>
        <div
          css={{
            width: "100%",
            height: 0,
            borderBottom: `1px solid ${hovered ? color.border.blue : color.border.default}`,
          }}
        />
      </button>
    );
  };

  return (
    <QuestionnaireInputDraftContext.Provider value={inputDraftRegistry}>
      {renderPopupWarningDisplayStrategyForbiddenOperation && (
        <PopupWarningDisplayStrategyForbiddenOperation
          {...{ handleClosePopup: handleClosePopupWarningDisplayStrategyForbiddenOperation }}
        />
      )}
      <div
        onClick={handleClickBackground}
        id="questionnaireBackground"
        css={{
          width: "100vw",
          height: "100vh",
          background: color.background,
          paddingBottom: pad.xl,
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 103,
          overflowY: "scroll",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {renderPopupReorderSection && (
          <PopupReorderQuestionnaireSection
            {...{
              setRenderPopupReorderSection,
              tempSectionList,
              setTempSectionList,
              upsertDisplayStrategyList,
            }}
          />
        )}

        {renderPopupEditDisplayLogic && (
          <PopupEditQuestionnaireDisplayLogic
            {...{
              setRenderPopupEditDisplayLogic,
              targetType: displayLogicTargetType,
              itemId: editingLogicItemId,
              upsertDisplayStrategyList,
              setUpsertDisplayStrategyList,
              tempSectionList,
              draft: draft && {
                readerRef: displayLogicReaderRef,
                onChange: () => draftOnChangeRef.current?.(),
                restore: displayLogicRestore,
              },
            }}
          />
        )}

        {RIF(
          renderPopupConfirmDiscard,
          <PopupConfirmDiscardQuestionnaireChanges
            {...{
              closeAction: setRenderPopupConfirmDiscard,
              confirmAction: closeAction,
            }}
          />,
        )}
        <main
          css={{
            width: "800px",
            paddingTop: "90px",
          }}
        >
          <div
            css={{
              paddingBottom: "150px",
              display: "flex",
              flexDirection: "column",
              gap: "40px",
            }}
          >
            {tempSectionList.map((section, index) => (
              <>
                <QuestionnaireSectionV2
                  {...{
                    key: section.id,
                    id: section.id,
                    index,
                    section,
                    editingBlock,
                    setEditingBlock,
                    addQuestion,
                    viewOnly,
                    tempSectionList,
                    setTempSectionList,
                    setRenderPopupReorderSection,
                    setRenderPopupEditDisplayLogic,
                    setDisplayLogicTargetType,
                    setEditingLogicItemId,
                    upsertDisplayStrategyList,
                    setRenderPopupWarningDisplayStrategyForbiddenOperation,
                  }}
                />
                {RIF(!viewOnly, <AddSectionButton {...{ index }} />)}
              </>
            ))}
          </div>
        </main>
        <SaveBar
          {...{
            handleClickSave: saveQuestionnaire,
            closeAction: handleCloseWithoutSaving,
            title: questionnaire?.name ?? "New Questionnaire",
            viewOnly: viewOnly,
          }}
        />
      </div>
    </QuestionnaireInputDraftContext.Provider>
  );
};
